/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { ArrowIcon, CategoryIcon, CalanderIcon } from '@/src/assets/icons'
import SelectFilter from '@/src/components/Filter-Component/components/SelectFilter'
import { NEWS_INSIGHTS_TYPE_OPTIONS } from '@/src/components/Filter-Component/utils'
import TruncatedAnswer from '@/src/components/HomepageEdit/components/TruncatedElement'
import CustomSearchBar from '@/src/components/searchbar'
import { useDebounceCallback } from '@/src/types/UseDebounceHook'
import { Box, Chip, CircularProgress, Typography } from '@mui/material'
import CommonApi from '@/pages/api'
import Link from 'next/link'
import { formatDate } from '@/src/types/helper'
import ContentThumbnail from '@/src/components/ContentThumbnail'
import { AnnouncementEndpoint } from '@/pages/api/endpoints'
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled'
import dummyImage from '@/src/assets/dummy/dummy.png'
import { getAnnouncementUrl } from '@/src/types/helper'
import { useRouter } from 'next/router'

// ?media=… deep-link values → the media type values the API expects.
const MEDIA_QUERY_MAP: Record<string, string> = {
  article: 'web',
  web: 'web',
  video: 'video',
  pdf: 'pdf',
  content: 'content'
}

const VALID_TYPE_IDS = NEWS_INSIGHTS_TYPE_OPTIONS.map(option => String(option.id))

const parseTypesParam = (value: string | string[] | undefined): string[] => {
  const raw = Array.isArray(value) ? value[0] : value
  if (!raw) return []
  return raw
    .split(',')
    .map(item => item.trim().toLowerCase())
    .filter(item => VALID_TYPE_IDS.includes(item))
}

const NewsInsights = () => {
  const router = useRouter()
  const mediaParam = Array.isArray(router.query.media)
    ? router.query.media[0]
    : router.query.media
  const deepLinkedType = mediaParam ? MEDIA_QUERY_MAP[mediaParam.toLowerCase()] : undefined

  const [groupedResults, setGroupedResults] = useState<Record<string, any[]>>({})
  const [selectedType, setSelectedType] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [tagsList, setTagsList] = useState<string[]>([])
  const tagOptions = useMemo(
    () => tagsList.map(tag => ({ id: tag, name: tag, value: tag })),
    [tagsList]
  )
  const [years, setYears] = useState<string[]>([''])
  const [yearsList, setYearsList] = useState<any>([])
  const [page, setPage] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [hasMore, setHasMore] = useState(true)
  // Holds the first fetch until router.query is populated, otherwise a
  // ?media=… / ?types=… deep-link would fire an unfiltered request first.
  const [hydrated, setHydrated] = useState(false)

  const observer = useRef<IntersectionObserver | null>(null)
  // Only the latest request may write results — a slower earlier response
  // would otherwise merge its rows into the newly filtered list.
  const requestIdRef = useRef(0)

  const fetchPageData = useCallback(
    async (searchKey?: string, pageNumber = 1) => {
      const requestId = ++requestIdRef.current
      setLoading(true)
      try {
        const response = await CommonApi({
          path: AnnouncementEndpoint?.get({
            types: selectedType && selectedType?.length > 0 ? JSON.stringify(selectedType) : '',
            searchValue: searchKey ?? '',
            fromDate: '',
            toDate: '',
            category: 'update',
            iress_category: [],
            filter: '',
            years: years[0] === 'All Years' ? [] : years,
            page: pageNumber,
            limit: 12,
            tags: selectedTags
          }),
          method: 'get'
        })

        if (requestIdRef.current !== requestId) return

        const newResults = response?.data?.results || {}
        setGroupedResults(prev => {
          const merged = { ...prev }
          Object.entries(newResults).forEach(([date, items]: any) => {
            merged[date] = [...(merged[date] || []), ...items]
          })
          return merged
        })
        const yearsData = response.data.years_list
          .sort((a: number, b: number) => b - a) // sort descending
          .map((year: number) => ({
            name: year,
            id: year,
            value: year
          }))
        setYearsList(yearsData)
        setTagsList(response.data.tags_list ?? [])
        setHasMore(response?.data?.next)
        setPage(pageNumber)
      } catch (error) {
        console.log(error)
      } finally {
        if (requestIdRef.current === requestId) setLoading(false)
      }
    },
    [selectedType, years, selectedTags]
  )

  const handleSearch = useDebounceCallback((value: any) => {
    resetAndFetch(value)
  }, 500)

  const resetAndFetch = useCallback(
    (searchVal?: string) => {
      setGroupedResults({})
      setPage(1)
      setHasMore(true)
      fetchPageData(searchVal, 1)
    },
    [setGroupedResults, setPage, setHasMore, fetchPageData]
  )

  const lastElementRef = useCallback(
    (node: any) => {
      if (loading || (page === 1 && Object.keys(groupedResults).length === 0)) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          fetchPageData(undefined, page + 1)
        }
      })

      if (node) observer.current.observe(node)
    },
    [loading, page, hasMore, fetchPageData, groupedResults]
  )

  const syncTypesToQuery = useCallback(
    (nextTypes: string[]) => {
      const query = { ...router.query }
      // ?media=… is only an entry point; the filter state lives in ?types=…
      delete query.media
      if (nextTypes.length > 0) {
        query.types = nextTypes.join(',')
      } else {
        delete query.types
      }
      router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
        scroll: false
      })
    },
    [router]
  )

  useEffect(() => {
    if (!router.isReady) return
    const typesFromQuery = parseTypesParam(router.query.types)
    // deepLinkedType stays in effect until syncTypesToQuery lands and drops
    // ?media=… — otherwise the re-run right after `hydrated` flips would reset
    // the filter to [] and fire an unfiltered request.
    const nextTypes =
      typesFromQuery.length > 0 ? typesFromQuery : deepLinkedType ? [deepLinkedType] : []
    setSelectedType(prev => (prev.join(',') === nextTypes.join(',') ? prev : nextTypes))
    if (!hydrated) {
      setHydrated(true)
      if (mediaParam) syncTypesToQuery(nextTypes)
    }
  }, [router.isReady, router.query.types, deepLinkedType, mediaParam, hydrated, syncTypesToQuery])

  const handleTypeChange = useCallback(
    (value: unknown) => {
      const nextTypes = Array.isArray(value) ? value.map(item => String(item)) : []
      setSelectedType(nextTypes)
      syncTypesToQuery(nextTypes)
    },
    [syncTypesToQuery]
  )

  useEffect(() => {
    if (!hydrated) return
    resetAndFetch()
  }, [hydrated, selectedType, years, selectedTags, resetAndFetch])

  return (
    <Box className="w-full py-8 px-6">
      <Box className="flex flex-col gap-6">
        <Box className="lg:px-[80px] xl:px-[100px] 2xl:px-[140px] flex flex-col sm:flex-row gap-2 justify-start sm:justify-between">
          <Box className="flex flex-wrap gap-2 min-w-0">
            <SelectFilter
              // Remount once router.query resolves so the deep-linked
              // media type shows up as pre-selected in the dropdown.
              key={`media-type-${hydrated ? 'ready' : 'pending'}`}
              options={NEWS_INSIGHTS_TYPE_OPTIONS}
              label="Media type"
              leftIcon={<CategoryIcon className="w-5 h-5 fill-primary-main" />}
              rightIcon={<ArrowIcon className="w-[12px] h-[12px] rotate-90 fill-primary-main" />}
              minWidth="173px"
              multiple={true}
              defaultValue={selectedType}
              onSelectionChange={handleTypeChange}
            />
            <SelectFilter
              options={tagOptions}
              label="Tags"
              leftIcon={<CategoryIcon className="w-5 h-5 fill-primary-main" />}
              rightIcon={<ArrowIcon className="w-[12px] h-[12px] rotate-90 fill-primary-main" />}
              minWidth="140px"
              menuMaxHeight={320}
              wrapOptionText
              multiple={true}
              emptyText="No tags yet"
              onSelectionChange={(ids: string[]) => setSelectedTags(ids ?? [])}
            />
            <SelectFilter
              options={yearsList}
              label="Years published"
              leftIcon={<CalanderIcon className="w-5 h-5 fill-primary-main" />}
              rightIcon={<ArrowIcon className="w-[12px] h-[12px] rotate-90 fill-primary-main" />}
              minWidth="198px"
              multiple={true}
              onSelectionChange={setYears}
            />
          </Box>
          <CustomSearchBar
            onSearch={handleSearch}
            placeholder="Search"
            classes="!rounded-[50px] text-black-800  text-2xs  !h-[44px]"
          />
        </Box>

        <Box className="grid gap-6 lg:px-[80px] xl:px-[100px] 2xl:px-[140px]">
          <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4 gap-6">
            {groupedResults &&
              Object.values(groupedResults)
                .flat() // flatten all entries into one array
                .map((item: any, index: number) => (
                  <Link
                    key={item?.created_on + index}
                    href={getAnnouncementUrl(item?.headline, item?.id)}
                  >
                    <div className="relative bg-white rounded-2xl flex flex-col h-full shadow-md">
                      {item?.types === 'video' && (
                        <PlayCircleFilledIcon
                          sx={{
                            transform: 'translate(-50%, -50%)',
                            top: '22%',
                            left: '50%',
                            cursor: 'pointer'
                          }}
                          className="object-cover absolute w-[78px] h-[78px] text-[#FFFFFFCC]"
                        />
                      )}

                      <ContentThumbnail
                        item={item}
                        alt="Company Thumbnail"
                        className="rounded-t-2xl w-full h-[190px] object-cover"
                      />

                      <div className="flex flex-col gap-4 flex-grow p-4">
                        <div className="flex flex-col gap-2">
                          <Typography variant="caption">
                            {formatDate(item?.display_date, 'long')}
                          </Typography>
                          <TruncatedAnswer content={item?.headline} textType="subtitle1" maxLength={65} />
                        </div>
                        <div className="mt-auto">
                          <div className="flex justify-between items-center gap-3">
                            <div className="w-full flex gap-3 items-center">
                              {item?.source_icon && (
                                <div className="rounded-full">
                                  <img
                                    src={
                                      typeof item?.source_icon === 'string'
                                        ? item?.source_icon
                                        : dummyImage.src
                                    }
                                    alt={`${item?.source} icon`}
                                    className="rounded-full object-cover !w-[32px] h-[32px]"
                                  />
                                </div>
                              )}
                              <Typography variant="subtitle2">{item?.source_name ?? ''}</Typography>
                            </div>
                            <Chip
                              variant="filled"
                              size="small"
                              color="default"
                              label={
                                <Typography variant="chipLabel">
                                  {item?.types === 'web'
                                    ? 'Article'
                                    : item?.types === 'video'
                                      ? 'Video'
                                      : item?.types}
                                </Typography>
                              }
                              className="rounded-md capitalize"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
          </Box>

          {loading && (
            <Box
              className={`${
                Object.entries(groupedResults)?.length === 0 && 'h-[60vh]'
              } w-full flex justify-center items-center `}
            >
              <CircularProgress size={25} />
            </Box>
          )}

          <div ref={lastElementRef} className="h-[40px] w-full" />
        </Box>
      </Box>
    </Box>
  )
}

export default NewsInsights
